const REVIEW_WORKER_URL = 'https://yunicrafts-judgeme-sync.sbw12315.workers.dev';
const requestCache = new Map();
const surfaceState = new WeakMap();

function safeHttpUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
}

function safeInternalUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith('/') ? url.toString() : '';
  } catch {
    return '';
  }
}

function reviewText(value, fallback = '') {
  const text = value === null || value === undefined ? '' : String(value).trim();
  return text || fallback;
}

function reviewRating(value) {
  const rating = Number(value);
  return Number.isFinite(rating) ? Math.max(1, Math.min(5, Math.round(rating))) : 5;
}

function requestKey({ limit, cursor, media }) {
  return `${limit}|${cursor || ''}|${media || 'all'}`;
}

function fetchReviews({ limit, cursor = '', media = 'all' }) {
  const key = requestKey({ limit, cursor, media });
  if (requestCache.has(key)) return requestCache.get(key);

  const endpoint = new URL('/v1/reviews', REVIEW_WORKER_URL);
  endpoint.searchParams.set('limit', String(limit));
  endpoint.searchParams.set('media', media);
  if (cursor) endpoint.searchParams.set('cursor', cursor);

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 2600);
  const request = fetch(endpoint, {
    headers: { Accept: 'application/json' },
    credentials: 'omit',
    signal: controller.signal,
  }).then((response) => {
    if (!response.ok) throw new Error(`Review Worker returned ${response.status}`);
    return response.json();
  }).finally(() => window.clearTimeout(timeoutId));
  requestCache.set(key, request);
  return request;
}

function createStars(rating) {
  const value = reviewRating(rating);
  const stars = document.createElement('div');
  stars.className = 'yc-home-rating';
  stars.setAttribute('aria-label', `${value} out of 5 stars`);
  stars.textContent = `${'★'.repeat(value)}${'☆'.repeat(5 - value)}`;
  return stars;
}

function appendReviewMedia(container, review, surface) {
  const imageUrl = safeHttpUrl(review.image_url);
  const videoUrl = safeHttpUrl(review.video_url);
  const srcset = reviewText(review.image_srcset);
  const mediaLabel = reviewText(review.product_title, 'Customer review media');

  if (videoUrl) {
    const videoHost = new URL(videoUrl).hostname;
    const isEmbed = ['iframe.videodelivery.net', 'player.vimeo.com', 'www.youtube.com', 'www.youtube-nocookie.com'].includes(videoHost);
    if (isEmbed) {
      const iframe = document.createElement('iframe');
      iframe.src = videoUrl;
      iframe.title = mediaLabel;
      iframe.loading = 'lazy';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('aria-label', mediaLabel);
      container.appendChild(iframe);
      return;
    }

    const video = document.createElement('video');
    video.controls = true;
    video.preload = 'none';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('aria-label', mediaLabel);
    if (imageUrl) video.poster = imageUrl;
    const source = document.createElement('source');
    source.src = videoUrl;
    video.appendChild(source);
    container.appendChild(video);
    return;
  }

  if (imageUrl) {
    const image = document.createElement('img');
    image.src = imageUrl;
    image.alt = mediaLabel;
    image.loading = 'lazy';
    image.decoding = 'async';
    if (srcset) {
      image.srcset = srcset;
      image.sizes = surface === 'home' ? '(min-width: 990px) 13vw, 22vw' : '(min-width: 990px) 31vw, 92vw';
    }
    container.appendChild(image);
    return;
  }

  const empty = document.createElement('div');
  empty.className = 'yc-review-card__media-empty';
  empty.textContent = 'Customer story';
  container.appendChild(empty);
}

function createReviewCard(review, surface, index) {
  const card = document.createElement('article');
  card.className = surface === 'home' ? 'yc-home-review-card' : 'yc-review-page-card';
  card.dataset.ycReviewId = reviewText(review.id);
  if (surface === 'home' && index === 1) card.classList.add('yc-home-review-card--offset');

  const media = document.createElement('div');
  media.className = surface === 'home' ? 'yc-home-review-card__media' : 'yc-review-page-card__media';
  appendReviewMedia(media, review, surface);

  const body = document.createElement('div');
  body.className = surface === 'home' ? 'yc-home-review-card__body' : 'yc-review-page-card__body';
  body.appendChild(createStars(review.rating));

  const titleText = reviewText(review.title, 'Customer review');
  if (surface === 'home' && titleText.toLowerCase() === 'customer review') {
    // A generic title adds nothing on the home wall; let the quote lead.
    card.classList.add('yc-home-review-card--quote');
  } else {
    const title = document.createElement(surface === 'home' ? 'h3' : 'h2');
    title.textContent = titleText;
    body.appendChild(title);
  }

  const quote = document.createElement('p');
  quote.textContent = reviewText(review.body);
  body.appendChild(quote);

  const meta = document.createElement('div');
  meta.className = surface === 'home' ? 'yc-home-review-card__meta' : 'yc-review-page-card__meta';
  const name = reviewText(review.reviewer_name, 'Verified customer');
  meta.textContent = name;
  if (review.verified === true) {
    const verified = document.createElement('span');
    verified.className = 'yc-review-verified';
    verified.textContent = 'Verified purchase';
    meta.append(' · ', verified);
  }
  body.appendChild(meta);

  const productUrl = safeInternalUrl(review.product_url);
  const productTitle = reviewText(review.product_title);
  if (productUrl && productTitle) {
    const productLink = document.createElement('a');
    productLink.className = surface === 'home' ? 'yc-home-review-card__product' : 'yc-review-page-card__product';
    productLink.href = productUrl;
    productLink.textContent = productTitle;
    productLink.setAttribute('aria-label', `Shop ${productTitle}`);
    body.appendChild(productLink);
  }

  card.append(media, body);
  return card;
}

function updatePageSummary(surface, payload) {
  const average = Number(payload.average_rating);
  const total = Number(payload.total);
  surface.querySelectorAll('[data-yc-review-average]').forEach((element) => {
    if (Number.isFinite(average) && average > 0) element.textContent = average.toFixed(2);
  });
  surface.querySelectorAll('[data-yc-review-total]').forEach((element) => {
    if (Number.isFinite(total) && total >= 0) element.textContent = total.toLocaleString();
  });
}

function renderReviews(state, payload, append = false) {
  const list = state.surface.querySelector('[data-yc-review-list]');
  if (!(list instanceof HTMLElement) || !Array.isArray(payload.reviews) || !payload.reviews.length) return;
  const fragment = document.createDocumentFragment();
  payload.reviews.forEach((review, index) => fragment.appendChild(createReviewCard(review, state.surfaceType, index)));
  if (append) list.appendChild(fragment);
  else list.replaceChildren(fragment);
  state.cursor = payload.pagination?.next_cursor || '';
  state.hasMore = payload.pagination?.has_more === true;
  state.surface.dataset.ycReviewLoaded = 'true';
  const status = state.surface.querySelector('[data-yc-review-status]');
  if (status instanceof HTMLElement) status.hidden = true;
  updatePageSummary(state.surface, payload);
  const button = state.surface.querySelector('[data-yc-review-load-more]');
  if (button instanceof HTMLButtonElement) {
    button.hidden = !state.hasMore;
    button.disabled = false;
  }
}

async function loadFallback(state) {
  const list = state.surface.querySelector('[data-yc-review-list]');
  const fallbackUrl = state.surface.dataset.ycReviewFallback;
  if (!(list instanceof HTMLElement) || !fallbackUrl) return;
  try {
    const response = await fetch(fallbackUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) return;
    const payload = await response.json();
    if (!Array.isArray(payload.reviews)) return;
    // Home wall: honour the section's "cards to show" limit, and never let the
    // dated snapshot overwrite the live Judge.me count rendered by Liquid.
    const isHome = state.surfaceType === 'home';
    renderReviews(state, {
      reviews: isHome ? payload.reviews.slice(0, state.limit) : payload.reviews,
      total: isHome ? undefined : payload.source_review_count || payload.reviews.length,
      average_rating: payload.average_rating,
      pagination: { has_more: false, next_cursor: null },
    });
  } catch (error) {
    console.warn('Yunicrafts review fallback asset is unavailable', error);
  }
}

async function loadSurface(state, append = false) {
  if (state.loading || (append && !state.hasMore)) return;
  state.loading = true;
  const button = state.surface.querySelector('[data-yc-review-load-more]');
  if (button instanceof HTMLButtonElement) button.disabled = true;
  try {
    const payload = await fetchReviews({
      limit: state.limit,
      cursor: append ? state.cursor : '',
      media: state.media,
    });
    if (payload?.schema_version !== 1 || payload?.source !== 'judgeme') throw new Error('Invalid review Worker schema');
    renderReviews(state, payload, append);
  } catch (error) {
    console.warn('Yunicrafts review Worker is unavailable; keeping server fallback cards', error);
    const status = state.surface.querySelector('[data-yc-review-status]');
    if (status instanceof HTMLElement) status.hidden = true;
    if (button instanceof HTMLButtonElement) button.hidden = true;
    await loadFallback(state);
  } finally {
    state.loading = false;
    if (button instanceof HTMLButtonElement && !state.hasMore) button.disabled = false;
  }
}

function observeSurface(state) {
  if (!('IntersectionObserver' in window)) {
    loadSurface(state);
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    loadSurface(state);
  }, { rootMargin: state.surfaceType === 'home' ? '360px 0px' : '240px 0px' });
  observer.observe(state.surface);
}

function bindSurface(surface) {
  if (!(surface instanceof HTMLElement) || surfaceState.has(surface)) return;
  const surfaceType = surface.dataset.ycReviewSurface;
  if (!['home', 'page'].includes(surfaceType)) return;
  const state = {
    surface,
    surfaceType,
    limit: Number(surface.dataset.ycReviewLimit) || (surfaceType === 'home' ? 6 : 12),
    media: surface.dataset.ycReviewMedia || 'all',
    cursor: '',
    hasMore: false,
    loading: false,
  };
  surfaceState.set(surface, state);
  const button = surface.querySelector('[data-yc-review-load-more]');
  if (button instanceof HTMLButtonElement) {
    button.addEventListener('click', () => loadSurface(state, true));
  }
  observeSurface(state);
}

function mount(root = document) {
  if (root instanceof HTMLElement && root.matches('[data-yc-review-surface]')) bindSurface(root);
  root.querySelectorAll?.('[data-yc-review-surface]').forEach(bindSurface);
}

function boot() {
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
